import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikValues } from 'formik';

import {
  IUseSetGroupEditTable,
  SetGroupEditTableMode,
} from '../../../components/reusable/set-group-edit-table/interfaces';
import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { IExerciseResultItem } from '../../../redux/api/exercises/interfaces';
import { ExerciseOptionType } from '../../../shared/constants';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { orderBy, replace } from '../../../utils/collections/array-utils';
import {
  changeSetNumber,
  getEmptyTrainingSet,
  orderSets,
} from '../../../utils/training/set-utils';

export interface IUseEditTableTrainingContext {
  exerciseResults?: IExerciseResultItem[] | null;
}

function useEditTable(
  formik: FormikValues,
  context?: IUseEditTableTrainingContext,
): IUseSetGroupEditTable<ITrainingSet> {
  const { t } = useTranslation();
  const { showSaveSetButton, showUploadSetButton } = useUserSetPreferences();
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();

  const onChangeSet = useCallback(
    (prevSets: ITrainingSet[], set: ITrainingSet) => {
      return replace(prevSets, set, ['setNumber', 'orderNumber']);
    },
    [],
  );

  const onRemoveSetsBySetNumber = useCallback(
    (prevSets: ITrainingSet[], setNumber: number, orderNumber?: number) => {
      let currentSets = orderBy<ITrainingSet>(
        [...prevSets],
        ['setNumber', 'orderNumber'],
      );

      const setsWithSameSetNumberCount = currentSets.filter(
        (set) => set.setNumber === setNumber,
      )?.length;

      if (orderNumber !== undefined && orderNumber !== null) {
        currentSets = currentSets.filter(
          (set) =>
            set.setNumber !== setNumber || set.orderNumber !== orderNumber,
        );
      } else {
        currentSets = currentSets.filter((set) => set.setNumber !== setNumber);
      }

      currentSets = currentSets
        .map((set) => {
          if (set.setNumber === setNumber && set.orderNumber > orderNumber) {
            return { ...set, orderNumber: set.orderNumber - 1 };
          }

          if (
            set.setNumber > setNumber &&
            (orderNumber === undefined ||
              orderNumber === null ||
              setsWithSameSetNumberCount === 1)
          ) {
            return { ...set, setNumber: set.setNumber - 1 };
          }

          return set;
        })
        .filter(Boolean);

      return currentSets;
    },
    [],
  );

  const onAddSet = useCallback(
    (prevSets: ITrainingSet[], newSet?: ITrainingSet, trainingId?: string) => {
      let currentSets = [...prevSets];

      if (newSet) {
        currentSets = [...currentSets, newSet];
      } else {
        const maxSetNumber = currentSets?.length
          ? Math.max(...currentSets.map((set) => set.setNumber))
          : -1;

        currentSets = [
          ...currentSets,
          getEmptyTrainingSet(
            maxSetNumber + 1,
            0,
            formik.values.trainingId || null,
            defaultRestForNewEmptyItem,
          ),
        ];
      }

      currentSets = orderSets(currentSets);

      return currentSets;
    },
    [formik.values.trainingId, defaultRestForNewEmptyItem],
  );

  const onChangeSetOrder = useCallback(
    (prevSets: ITrainingSet[], setNumber: number, direction: 'up' | 'down') => {
      let currentSets = [...prevSets];

      currentSets = changeSetNumber(currentSets, setNumber, direction);

      return currentSets;
    },
    [],
  );

  const onChangeSetWrapper = useCallback(
    (set: ITrainingSet) => {
      formik.setValues((prev) => ({
        ...prev,
        trainingSets: onChangeSet(prev.trainingSets, set),
      }));
    },
    [onChangeSet],
  );

  const onRemoveSetsBySetNumberWrapper = useCallback(
    (setNumber: number, orderNumber?: number) => {
      formik.setValues((prev) => ({
        ...prev,
        trainingSets: onRemoveSetsBySetNumber(
          prev.trainingSets,
          setNumber,
          orderNumber,
        ),
      }));
    },
    [onRemoveSetsBySetNumber],
  );

  const onAddSetWrapper = useCallback(
    (newSet?: ITrainingSet) => {
      formik.setValues((prev) => ({
        ...prev,
        trainingSets: onAddSet(prev.trainingSets, newSet, prev.trainingId),
      }));
    },
    [onAddSet],
  );

  const onChangeSetOrderWrapper = useCallback(
    (setNumber: number, direction: 'up' | 'down') => {
      formik.setValues((prev) => ({
        ...prev,
        trainingSets: onChangeSetOrder(prev.trainingSets, setNumber, direction),
      }));
    },
    [onChangeSetOrder],
  );

  return {
    options: {
      hideSuperset: false,
      hideSetsCount: true,
      hideExpand: false,
      hideHeader: false,
      hideSavedSetUploading: !showUploadSetButton,
      removeSetMode: 'both',
      hideSaveSet: !showSaveSetButton,
    },
    exerciseResults: context?.exerciseResults,
    mode: SetGroupEditTableMode.training,
    maxSetNumber: Math.max(
      ...(formik.values.trainingSets?.map((set) => set.setNumber) || [0]),
    ),
    submitCalled: formik.submitCount > 0,
    optionValidators: [
      {
        message: t('formikValidation.minValue').replace('{0}', '0'),
        validator: (value: number) => {
          return value && value < 0;
        },
      },
      {
        type: ExerciseOptionType.integer,
        message: t('formikValidation.integerValueRequired'),
        validator: (value: number) => {
          return +value === value && !Number.isInteger(value);
        },
      },
    ],
    onChangeSet: onChangeSetWrapper,
    onRemoveSetsBySetNumber: onRemoveSetsBySetNumberWrapper,
    onAddSet: onAddSetWrapper,
    onChangeSetOrder: onChangeSetOrderWrapper,
  };
}

export default useEditTable;
